/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import { TlsProxy, TlsProxyListener } from 'data/tlsProxy/tlsProxy.types';
import { NodeData } from 'data/nodes/nodes.types';
import { TextSuggestType } from 'shared/form/custom/TextSuggest/TextSuggest';
import { DistributorConfigurationListenerAfWhiteList } from 'data/distributorConfiguration/distributorConfiguration.types';
import i18n from 'i18n';
import { ipAddressValidation, fqdnValidation } from '../templates/distributorTemplateFieldsValidation';

export type ListenersIpAddressPortsType = {
  [key: string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
  owner?: string,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'create') {
    (clonedTemplate.listeners as FormTemplateSection).fields.status.hide = 'true';
    (clonedTemplate.destinations as FormTemplateSection).fields.status.hide = 'true';
  } else if (mode === 'edit') {
    if (owner === 'lis') {
      setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, ['requiredState']);
    } else {
      (clonedTemplate.main as FormTemplateSection).fields.moduleName.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.instanceId.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.afType.readOnly = true;
    }
    (clonedTemplate.listeners as FormTemplateSection).fields.status.hide = 'false';
    (clonedTemplate.destinations as FormTemplateSection).fields.status.hide = 'false';
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
    (clonedTemplate.listeners as FormTemplateSection).fields.status.hide = 'false';
    (clonedTemplate.destinations as FormTemplateSection).fields.status.hide = 'false';
  } else if (mode === 'copy') {
    (clonedTemplate.main as FormTemplateSection).fields.moduleName.readOnly = true;
    (clonedTemplate.listeners as FormTemplateSection).fields.status.hide = 'true';
    (clonedTemplate.destinations as FormTemplateSection).fields.status.hide = 'true';
  }

  return clonedTemplate;
}

export function adaptListenersIpAddressPorts(tlsProxies: TlsProxy[]): ListenersIpAddressPortsType {
  return (tlsProxies || [])
    .map(selectListeners)
    .reduce(flatListeners, [])
    .reduce(groupPortsByIpAddress, {});
}

export function adaptDestinationsIpAddressesOptions(
  listenersIpAddressPorts: ListenersIpAddressPortsType,
): TextSuggestType[] {
  return Object.keys(listenersIpAddressPorts).map((ipAddress) => ({
    label: ipAddress,
    value: ipAddress,
  }));
}

export function adaptDestinationsPortsOptions(
  ipAddress: string,
  listenersIpAddressPorts: ListenersIpAddressPortsType,
): TextSuggestType[] {
  const ipAddressPorts = (listenersIpAddressPorts || {})[ipAddress] || [];
  return ipAddressPorts.map((port: string) => ({ label: port, value: port }));
}

export const adaptNodeNameIpAddressOptions = (selectedNodeId: string, nodeNames: NodeData[]): TextSuggestType[] => {
  const selectedNode = nodeNames.find((node) => node.nodeName === selectedNodeId);
  if (!selectedNode) {
    return [];
  }

  return [
    {
      label: selectedNode.ipAddress,
      value: selectedNode.ipAddress,
    },
  ];
};

export const adaptDestinationsFqdnTemplate = (
  moduleName: string,
  afType: string,
  destinationsTemplate: FormTemplateSection,
  afTagsToProtocolMap: any,
): FormTemplateSection => {
  const clonedDestinationsTemplate = cloneDeep(destinationsTemplate);
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.label = 'Destination IP Address';
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.validation = ipAddressValidation;
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.placeholder = 'Type or select IP Address...';

  if (afType !== '') {
    const afTagWithModuleName = afType.concat('#', moduleName)
    const afTag = afTagsToProtocolMap[afTagWithModuleName] ? afTagWithModuleName : afType;

    if (afTagsToProtocolMap[afTag]) {
      const protocol = afTagsToProtocolMap[afTag];
      if (protocol.destinationDefaultValues) {
        const fqdn: any = protocol.destinationDefaultValues.find((destinationDefaultValue: any) => destinationDefaultValue.field === 'destfqdn' && destinationDefaultValue.defaultValue);

        if (fqdn) {
          (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.label = 'Destination IP Address or FQDN';
          (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.validation = fqdnValidation;
          (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.placeholder = 'Type IP Address or FQDN...';
        }
      }
    }
  }

  return clonedDestinationsTemplate;
}

export const adaptFqdnTemplate = (
  destinationsTemplate: string | FormTemplateSection
): string | FormTemplateSection => {
  const clonedDestinationsTemplate = cloneDeep(destinationsTemplate);
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.label = 'Destination IP Address or FQDN';
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.validation = fqdnValidation;
  (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.placeholder = 'Type IP Address or FQDN...';

  return clonedDestinationsTemplate;
}

export const adaptDestinationsIpAddressPortTemplate = (
  moduleName: string,
  destinationsTemplate: FormTemplateSection,
): FormTemplateSection => {
  const clonedDestinationsTemplate = cloneDeep(destinationsTemplate);

  if (moduleName !== 'XCF3') {
    (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.type = 'text';
    (clonedDestinationsTemplate as FormTemplateSection).fields.port.type = 'text';
  } else {
    (clonedDestinationsTemplate as FormTemplateSection).fields.ipAddress.type = 'textSuggest';
    (clonedDestinationsTemplate as FormTemplateSection).fields.port.type = 'textSuggest';
  }

  return clonedDestinationsTemplate;
};

export const adaptAllowedIPAddressesLabel = (
  afWhitelist: DistributorConfigurationListenerAfWhiteList[],
): string => {

  if (!afWhitelist || !afWhitelist?.length) {
    return i18n.t('ss8Distributor.form.button.ipAddresses');
  }

  return i18n.t('ss8Distributor.form.label.ipAllowed', { count: afWhitelist?.length });
};

function selectListeners(item: TlsProxy) {
  return item.listeners;
}

function flatListeners(previous: TlsProxyListener[], current: TlsProxyListener[]): TlsProxyListener[] {
  return [...previous, ...current];
}

function groupPortsByIpAddress(
  groupObj: ListenersIpAddressPortsType,
  item: TlsProxyListener,
): ListenersIpAddressPortsType {

  const { ipAddress, port } = item;
  const currentIpAddressPorts = groupObj[ipAddress] || [];
  const updatedIpAddressPorts = [...currentIpAddressPorts, port];

  return { ...groupObj, [ipAddress]: updatedIpAddressPorts };

}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}
